import React, { useState, useContext, useEffect, useRef } from 'react';
import { SessionContext } from '../../common/SessionContext';
import axios from 'axios';
import { Link } from 'react-router-dom';
import ThreeDays from './ThreeDays';

const scrollTop = () => { window.scrollTo({ top: 0, behavior: "smooth" }) }


function BookingForm(props) {

  // API configuration constants
  const API_URL = `${process.env.REACT_APP_API_FOLDER}save_unpaid_booking/`;
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;

  const { session } = useContext(SessionContext);

  const currentCurrency = session.currency;
  const fullAmount = props.workshop?.pricing?.[currentCurrency]?.full_amount;
  const splitAmount = props.workshop?.pricing?.[currentCurrency]?.split_amount;
  const numberOfPayments = props.workshop?.pricing?.[currentCurrency]?.number_of_payments;
  const currencySymbol = props.workshop?.pricing?.[currentCurrency]?.symbol;

  const editions = props.workshop?.editions || [];

  // Form field states
  const firstAvailableEdition = editions.find(edition => edition.places > 0);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [aboutMe, setAboutMe] = useState('');
  // Set the edition to the first one with availability, otherwise default the first edition
  const [workshopEdition, setworkshopEdition] = useState(firstAvailableEdition ? firstAvailableEdition.id : editions[0].id);



  const asideRef = useRef(null); // create a ref for the container

  // Submission and background state
  const [loading, setLoading] = useState(false);
  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [success, setSuccess] = useState(false);
  const [bookingHash, setBookingHash] = useState(false);
  // This state triggers the transition to the final background color.
  const [bgTransition, setBgTransition] = useState(false);



  const handleSubmit = async (e) => {
    e.preventDefault();
    setButtonDisabled(true);
    setLoading(true);
    setErrorMsg('');

    // Prepare form data payload.
    const data = {
      first_name: firstName,
      last_name: lastName,
      email: email,
      workshop_edition: workshopEdition,
      about_me: aboutMe,
      currency: currentCurrency,
      ctype: props.workshop.ctype,
      course_id: props.workshop.id,
      pcv: props.workshop.pricecode_version,
      ws: session.ws
    };

    try {
      const response = await axios.post(API_URL, data, {
        headers: { "X-API-TOKEN": API_TOKEN },
        responseType: 'text'
      });
      setBookingHash(response.data.replace(/^"|"$/g, ''));
      // On success: update state so that the form is removed.
      setSuccess(true);
      setLoading(false);
      // Immediately set bg color to white, then trigger transition.
      setBgTransition(false);
      // Scroll the container into view
      const topPos = asideRef.current.getBoundingClientRect().top + window.pageYOffset - 60;
      window.scrollTo({ top: topPos, behavior: 'smooth' });
      // Use a timeout to trigger the background change.
      setTimeout(() => setBgTransition(true), 50);
    } catch (err) {
      const message = err.response?.data?.error || err.message;
      console.error("Error saving booking:", message);
      setErrorMsg(message);
      setLoading(false);
      setButtonDisabled(false);
    }
  };

  // Compute the style for the outer container when booking is successful.
  const asideStyle = success
    ? {
      background: bgTransition ? '#30A369' : '#ffffff',
      transition: 'background 1s ease'
    }
    : {};


  return (
    <>
      <aside
        ref={asideRef}
        className={`package-widget-style-2 widget-form ${success ? "shimmer-effect" : ""}`}
        style={asideStyle}
      >
        <div className="text-center d-flex justify-content-between">

          {!success && (
            <>
              <div className="book-title">
                <h4>Book Now</h4>
              </div>
              <h3 className="widget-lavel book-title-price">
                {props.workshop.places} <span>{props.workshop.placesword}</span>
              </h3>
            </>
          )}

        </div>

        <div className="widget-body">
          <div className="booktext">
            {!success && (
              <>
                The cost of this session is <strong>{currencySymbol}{fullAmount}</strong>
                {props.workshop.allow_pp == 1 && (
                  <> (or {numberOfPayments} payments of {currencySymbol}{splitAmount})</>
                )}
                <br /><br />To reserve, enter your details below - <strong>no payment is required at this stage</strong>.<br /><br />You'll receive a booking confirmation email with secure payment links, and you'll have three days to confirm your booking by making a payment.
              </>
            )}
            {errorMsg && <div className="error-text">{errorMsg}</div>}
          </div>

          {success ? (
            <div className="success-message">

              <h3><i className="fa-solid fa-mountain-sun"></i> You're all set!</h3>
              <p><strong>Thanks for your booking.</strong> Your place has been reserved, and you should get an email from us any minute now.</p>
              <p><strong>What happens next?</strong> You can confirm your booking right now by making a payment <Link to={`/confirm/${props.workshop.ctype}/${bookingHash}`} onClick={scrollTop}>here</Link>. Your place is held for <strong>three days</strong>, so you have until <ThreeDays /> to confirm by making a payment.</p>
              <p>If you have any questions about the workshop, the booking process or anything else, check the <Link to="/faq" onClick={scrollTop}>FAQ</Link>, or <a href="mailto:hello@campfr.com">drop us a line</a>.</p>
              <p>We're looking forward to welcoming you to CAMP!</p>

            </div>
          ) : (
            <form onSubmit={handleSubmit} id="booking-form">
              <div className="booking-form-wrapper">

                {editions.length === 1 ? (
                  <input
                    type="hidden"
                    name="workshop_edition"
                    value={editions[0].id}
                  />
                ) : (
                  <div className="custom-input-group">
                    <select
                      name="workshop_edition"
                      value={workshopEdition}
                      onChange={e => setworkshopEdition(e.target.value)}
                      required
                    >
                      <option value="">Select workshop start date</option>
                      {editions.map(edition =>
                        edition.places > 0 ? (
                          <option key={edition.id} value={edition.id}>
                            {edition.start_date} ({edition.places} places)
                          </option>
                        ) : (
                          <option key={edition.id} value={edition.id} disabled>
                            {edition.start_date} (SOLD OUT)
                          </option>
                        )
                      )}
                    </select>

                  </div>
                )}

                <div className="custom-input-group">
                  <input
                    type="text"
                    placeholder="Your first name"
                    id="first_name"
                    value={firstName}
                    onChange={e => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="custom-input-group">
                  <input
                    type="text"
                    placeholder="Your last name"
                    id="last_name"
                    value={lastName}
                    onChange={e => setLastName(e.target.value)}
                    required
                  />
                </div>
                <div className="custom-input-group">
                  <input
                    type="email"
                    placeholder="Your email"
                    id="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                  />
                </div>
                <div className="custom-input-group">
                  <textarea
                    cols={20}
                    rows={4}
                    placeholder="About you (optional, 200 words max)"
                    value={aboutMe}
                    onChange={e => setAboutMe(e.target.value)}
                  />
                </div>
                <div className="custom-input-group">
                  <div className="submite-btn">
                    <button type="submit" disabled={buttonDisabled}>
                      {loading ? <div className="spinner"></div> : 'Book Now'}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}
        </div>
      </aside>
    </>
  );
}

export default BookingForm;
